/**
 * Admin Clear Page - Session cleanup and system maintenance
 * Accessible only to super_admin users
 * @component AdminClearPage
 * @file AdminClearPage.tsx
 * @path frontend/src/pages/AdminClearPage/AdminClearPage.tsx
 */

import React, { useState, useEffect } from 'react';
import { config } from '../../config';
import LoadingSpinner from '../../components/LoadingSpinner';
import NotificationSnackbar from '../../components/NotificationSnackbar';
import { 
  Button, 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Grid, 
  Chip,
  LinearProgress,
  Alert,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination
} from '@mui/material';
import {
  CleaningServices as CleaningIcon,
  Refresh as RefreshIcon,
  Timer as TimerIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  DeleteForever as DeleteForeverIcon,
  Security as SecurityIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import './AdminClearPage.css';

interface SessionStats {
  total_sessions: number;
  active_sessions: number;
  expired_sessions: number;
  old_sessions: number;
  oldest_session?: string;
}

interface CleanupResult {
  message: string;
  type: string;
  deleted_count?: number;
}

interface FailedLoginAttempt {
  id: string;
  email: string;
  ip_address: string;
  attempt_time: string;
  user_agent?: string;
  success: boolean;
}

interface SupportMessage {
  id: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  created_at: string;
  status: 'new' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
}

const AdminClearPage: React.FC = () => {
  const [stats, setStats] = useState<SessionStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<Array<{
    id: string;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>>([]);
  const [cleanupInProgress, setCleanupInProgress] = useState(false);
  const [cleanupProgress, setCleanupProgress] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [failedAttempts, setFailedAttempts] = useState<FailedLoginAttempt[]>([]);
  const [failedAttemptsLoading, setFailedAttemptsLoading] = useState(false);
  const [supportMessages, setSupportMessages] = useState<SupportMessage[]>([]);
  const [supportMessagesLoading, setSupportMessagesLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchSessionStats();
  }, []);

  useEffect(() => {
    if (activeTab === 1) {
      fetchFailedAttempts();
    } else if (activeTab === 2) {
      fetchSupportMessages();
    }
  }, [activeTab, page]);

  const fetchFailedAttempts = async () => {
    try {
      setFailedAttemptsLoading(true);
      setError(null);

      // TODO: Implement backend endpoint
      // const response = await fetch(`${config.api.baseUrl}/admin/failed-login-attempts?page=${page}&limit=20`, {
      //   method: 'GET',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     Authorization: `Bearer ${localStorage.getItem(config.auth.tokenKey)}`,
      //   },
      // });

      // if (!response.ok) {
      //   const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      //   throw new Error(errorData.error || 'Failed to fetch failed login attempts');
      // }

      // const data = await response.json();
      // setFailedAttempts(data.attempts || []);
      // setTotalPages(data.total_pages || 1);

      // Temporary: Show development message
      setFailedAttempts([]);
      setTotalPages(1);
    } catch (err) {
      console.error('Error fetching failed attempts:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setFailedAttemptsLoading(false);
    }
  };

  const fetchSupportMessages = async () => {
    try {
      setSupportMessagesLoading(true);
      setError(null);

      // TODO: Implement backend endpoint
      // const response = await fetch(`${config.api.baseUrl}/admin/support-messages?page=${page}&limit=20`, {
      //   method: 'GET',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     Authorization: `Bearer ${localStorage.getItem(config.auth.tokenKey)}`,
      //   },
      // });

      // if (!response.ok) {
      //   const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      //   throw new Error(errorData.error || 'Failed to fetch support messages');
      // }

      // const data = await response.json();
      // setSupportMessages(data.messages || []);
      // setTotalPages(data.total_pages || 1);

      // Temporary: Show development message
      setSupportMessages([]);
      setTotalPages(1);
    } catch (err) {
      console.error('Error fetching support messages:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSupportMessagesLoading(false);
    }
  };

  const handleClearFailedAttempts = async () => {
    try {
      setCleanupInProgress(true);
      setCleanupProgress(0);

      const response = await fetch(`${config.api.baseUrl}/admin/failed-login-attempts/clear`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem(config.auth.tokenKey)}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Network error' }));
        throw new Error(errorData.error || 'Failed to clear failed login attempts');
      }

      const notificationId = Date.now().toString();
      setNotifications(prev => [...prev, {
        id: notificationId,
        message: 'Невдалі спроби входу успішно очищено',
        severity: 'success',
      }]);

      await fetchFailedAttempts();
    } catch (err) {
      console.error('Error clearing failed attempts:', err);
      const notificationId = Date.now().toString();
      setNotifications(prev => [...prev, {
        id: notificationId,
        message: err instanceof Error ? err.message : 'Помилка під час очищення',
        severity: 'error',
      }]);
    } finally {
      setCleanupInProgress(false);
    }
  };

  const handleClearSupportMessages = async () => {
    try {
      setCleanupInProgress(true);
      setCleanupProgress(0);

      const response = await fetch(`${config.api.baseUrl}/admin/support-messages/clear`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem(config.auth.tokenKey)}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Network error' }));
        throw new Error(errorData.error || 'Failed to clear support messages');
      }

      const notificationId = Date.now().toString();
      setNotifications(prev => [...prev, {
        id: notificationId,
        message: 'Повідомлення підтримки успішно очищено',
        severity: 'success',
      }]);

      await fetchSupportMessages();
    } catch (err) {
      console.error('Error clearing support messages:', err);
      const notificationId = Date.now().toString();
      setNotifications(prev => [...prev, {
        id: notificationId,
        message: err instanceof Error ? err.message : 'Помилка під час очищення',
        severity: 'error',
      }]);
    } finally {
      setCleanupInProgress(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setPage(1);
  };

  const fetchSessionStats = async () => {
    try {
      setStatsLoading(true);
      setError(null);

      const response = await fetch(`${config.api.baseUrl}/admin/sessions/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem(config.auth.tokenKey)}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Network error' }));
        throw new Error(errorData.error || 'Failed to fetch session statistics');
      }

      const data = await response.json();
      setStats(data.stats);
    } catch (err) {
      console.error('Error fetching session stats:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setStatsLoading(false);
    }
  };

  const handleCleanup = async (type: string = 'full') => {
    try {
      setCleanupInProgress(true);
      setCleanupProgress(0);
      setError(null);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setCleanupProgress((prev: number) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await fetch(`${config.api.baseUrl}/admin/sessions/cleanup?type=${type}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem(config.auth.tokenKey)}`,
        },
      });

      clearInterval(progressInterval);
      setCleanupProgress(100);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Network error' }));
        throw new Error(errorData.error || 'Failed to run cleanup');
      }

      const data: CleanupResult = await response.json();

      const notificationId = Date.now().toString();
      setNotifications((prev: Array<{id: string, message: string, severity: 'success' | 'error' | 'warning' | 'info'}>) => [...prev, {
        id: notificationId,
        message: data.message || `Очищення '${type}' виконано успішно`,
        severity: 'success',
      }]);

      // Refresh stats after cleanup
      await fetchSessionStats();
    } catch (err) {
      console.error('Error running cleanup:', err);
      const notificationId = Date.now().toString();
      setNotifications(prev => [...prev, {
        id: notificationId,
        message: err instanceof Error ? err.message : 'Помилка під час очищення',
        severity: 'error',
      }]);
    } finally {
      setCleanupInProgress(false);
      setTimeout(() => setCleanupProgress(0), 1000);
    }
  };

  const handleCloseNotification = (id: string) => {
    setNotifications((prev: Array<{id: string, message: string, severity: 'success' | 'error' | 'warning' | 'info'}>) => prev.filter((n: {id: string}) => n.id !== id));
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Немає даних';
    return new Date(dateString).toLocaleDateString('uk-UA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'error';
      case 'in_progress': return 'warning';
      case 'resolved': return 'success';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  if (statsLoading) {
    return (
      <div className="admin-clear-page">
        <div className="loading-container">
          <LoadingSpinner size="large" />
          <p>Завантаження статистики сесій...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-clear-page">
        <div className="error-container">
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h6">Помилка завантаження</Typography>
            <Typography>{error}</Typography>
          </Alert>
          <Button 
            variant="contained" 
            onClick={fetchSessionStats}
            startIcon={<RefreshIcon />}
          >
            Спробувати знову
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-clear-page">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h1>Очищення системи</h1>
            <p>Керування сесіями, невдалими спробами входу та повідомленнями підтримки</p>
          </div>
          <IconButton 
            onClick={() => activeTab === 0 ? fetchSessionStats() : activeTab === 1 ? fetchFailedAttempts() : fetchSupportMessages()}
            disabled={statsLoading || failedAttemptsLoading || supportMessagesLoading}
            title="Оновити дані"
          >
            <RefreshIcon />
          </IconButton>
        </div>
      </div>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<TimerIcon />} 
            label="Сесії" 
            iconPosition="start"
          />
          <Tab 
            icon={<SecurityIcon />} 
            label="Невдалі спроби входу" 
            iconPosition="start"
          />
          <Tab 
            icon={<MessageIcon />} 
            label="Повідомлення підтримки" 
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <>
          {/* Statistics Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card className="stat-card total">
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Всього сесій
                      </Typography>
                      <Typography variant="h4" component="div">
                        {stats?.total_sessions || 0}
                      </Typography>
                    </Box>
                    <InfoIcon className="stat-icon" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card className="stat-card active">
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Активні
                      </Typography>
                      <Typography variant="h4" component="div">
                        {stats?.active_sessions || 0}
                      </Typography>
                    </Box>
                    <CheckIcon className="stat-icon" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card className="stat-card expired">
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Прострочені
                      </Typography>
                      <Typography variant="h4" component="div">
                        {stats?.expired_sessions || 0}
                      </Typography>
                    </Box>
                    <TimerIcon className="stat-icon" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card className="stat-card old">
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                      <Typography color="textSecondary" gutterBottom variant="overline">
                        Старі (&gt;7 днів)
                      </Typography>
                      <Typography variant="h4" component="div">
                        {stats?.old_sessions || 0}
                      </Typography>
                    </Box>
                    <ErrorIcon className="stat-icon" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Cleanup Actions */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <CleaningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Очищення сесій
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Виконайте ручне очищення сесій для оптимізації бази даних. Автоматичне очищення відбувається щодня.
            </Typography>

            {cleanupInProgress && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" gutterBottom>
                  Виконується очищення...
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={cleanupProgress} 
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Tooltip 
                  title="Видаляє прострочені, неактивні та старі сесії"
                  arrow
                  placement="top"
                >
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    startIcon={<CleaningIcon />}
                    onClick={() => handleCleanup('full')}
                    disabled={cleanupInProgress}
                    size="large"
                  >
                    Повне очищення
                  </Button>
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Tooltip 
                  title="Видаляє тільки прострочені сесії"
                  arrow
                  placement="top"
                >
                  <Button
                    fullWidth
                    variant="outlined"
                    color="warning"
                    startIcon={<TimerIcon />}
                    onClick={() => handleCleanup('expired')}
                    disabled={cleanupInProgress}
                  >
                    Прострочені
                  </Button>
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Tooltip 
                  title="Видаляє неактивні сесії (старші за 1 день)"
                  arrow
                  placement="top"
                >
                  <Button
                    fullWidth
                    variant="outlined"
                    color="secondary"
                    startIcon={<DeleteForeverIcon />}
                    onClick={() => handleCleanup('inactive')}
                    disabled={cleanupInProgress}
                  >
                    Неактивні
                  </Button>
                </Tooltip>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Tooltip 
                  title="Видаляє сесії старші за 7 днів"
                  arrow
                  placement="top"
                >
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteForeverIcon />}
                    onClick={() => handleCleanup('old')}
                    disabled={cleanupInProgress}
                  >
                    Старі (&gt;7 днів)
                  </Button>
                </Tooltip>
              </Grid>
            </Grid>
          </Paper>

          {/* System Information */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Інформація про систему
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Найстаріша сесія:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {formatDate(stats?.oldest_session)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  Автоматичне очищення:
                </Typography>
                <Chip 
                  label="Щодня опівночі" 
                  color="success" 
                  size="small" 
                  variant="outlined"
                />
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" component="div">
                <strong>Рекомендації:</strong>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>Виконуйте повне очищення раз на тиждень для оптимальної продуктивності</li>
                  <li>Моніторьте кількість прострочених сесій</li>
                  <li>Автоматичне очищення запускається щодня, але ручне очищення може бути потрібне після пікових навантажень</li>
                </ul>
              </Typography>
            </Alert>
          </Paper>
        </>
      )}

      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">
              <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Невдалі спроби входу
            </Typography>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteForeverIcon />}
              onClick={handleClearFailedAttempts}
              disabled={cleanupInProgress || failedAttemptsLoading}
            >
              Очистити всі
            </Button>
          </Box>

          {failedAttemptsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <LoadingSpinner />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>IP адреса</TableCell>
                      <TableCell>Час спроби</TableCell>
                      <TableCell>Статус</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {failedAttempts.map((attempt) => (
                      <TableRow key={attempt.id}>
                        <TableCell>{attempt.email}</TableCell>
                        <TableCell>{attempt.ip_address}</TableCell>
                        <TableCell>{formatDate(attempt.attempt_time)}</TableCell>
                        <TableCell>
                          <Chip 
                            icon={attempt.success ? <CheckIcon /> : <ErrorIcon />}
                            label={attempt.success ? 'Успішно' : 'Невдало'}
                            color={attempt.success ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, newPage) => setPage(newPage)}
                    color="primary"
                  />
                </Box>
              )}

              {failedAttempts.length === 0 && (
                <Box textAlign="center" py={4}>
                  <Typography color="textSecondary">
                    Немає невдалих спроб входу
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Paper>
      )}

      {activeTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">
              <MessageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Повідомлення підтримки
            </Typography>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteForeverIcon />}
              onClick={handleClearSupportMessages}
              disabled={cleanupInProgress || supportMessagesLoading}
            >
              Очистити всі
            </Button>
          </Box>

          {supportMessagesLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <LoadingSpinner />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Ім'я</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Тема</TableCell>
                      <TableCell>Статус</TableCell>
                      <TableCell>Пріоритет</TableCell>
                      <TableCell>Створено</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {supportMessages.map((message) => (
                      <TableRow key={message.id}>
                        <TableCell>{message.name}</TableCell>
                        <TableCell>{message.email}</TableCell>
                        <TableCell>{message.subject}</TableCell>
                        <TableCell>
                          <Chip 
                            label={message.status}
                            color={getStatusColor(message.status) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={message.priority}
                            color={getPriorityColor(message.priority) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDate(message.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, newPage) => setPage(newPage)}
                    color="primary"
                  />
                </Box>
              )}

              {supportMessages.length === 0 && (
                <Box textAlign="center" py={4}>
                  <Typography color="textSecondary">
                    Немає повідомлень підтримки
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Paper>
      )}

      {/* Notifications */}
      <NotificationSnackbar
        notifications={notifications}
        onClose={handleCloseNotification}
      />
    </div>
  );
};

export default AdminClearPage;
